import { useEffect, useState } from "react";
import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Plus, X } from "lucide-react";
import Form from "../components/Form";
import CreateLeavePage from "./CreateLeavePage";
import * as XLSX from "xlsx";
import check from "../assets/check.svg";
import info from "../assets/info.svg";
import InfoModal from "../components/modal/InfoModal";
import RejectReasonModal from "../components/modal/RejectModal";
import { LeaveStateEnum, leaveStateKey } from "../modal/leave-state";
import { LEAVE_TYPES, leaveTypeLabel } from "../modal/leave-type";
import * as action from "../Store/_redux/LeaveStore/leaveAction";
import deleteIcon from "../assets/delete.svg";
import { useTranslation } from "react-i18next";
import { formatDate } from "../i18n";

const stateBadgeClass = {
  [LeaveStateEnum.APPROVED]: "text-green-500 bg-green-100",
  [LeaveStateEnum.REJECTED]: "text-red-500 bg-red-100",
  [LeaveStateEnum.PENDING]: "text-yellow-500 bg-yellow-100",
};

const bulkButtonClass = (color) =>
  `${color} text-white px-4 py-2 rounded-lg text-xs font-semibold shadow-sm disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none transition-colors active:scale-95 duration-150`;

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);

function List() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth.loginUser);
  const formLeave = useSelector((state) => state.leave.leaves);
  const isAdmin = auth?.userType === "admin";
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedInfo, setSelectedInfo] = useState(null);
  const [rejectingLeave, setRejectingLeave] = useState(null);

  const [filterName, setFilterName] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filtered, setFiltered] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [selectedIds, setSelectedIds] = useState([]);

  const [deleteDate, setDeleteDate] = useState("");

  useEffect(() => {
    handleRefresh();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filtered]);

  useEffect(() => {
    setFiltered(formLeave || []);
  }, [formLeave]);

  const handleRefresh = () => {
    dispatch(action.getLeave(auth));
  };

  const handleInfoClick = (contact) => setSelectedInfo(contact);
  const handleCloseModal = () => setSelectedInfo(null);

  const deletes = async (data) => {
    const confirmed = window.confirm(t("list.confirmDelete"));
    if (!confirmed) return;
    dispatch(action.deleteLeave(data));
  };

  const approves = async (data) => {
    const response = await dispatch(action.approve(data));
    if (response) handleRefresh();
  };

  const openRejectModal = (leave) => setRejectingLeave(leave);

  const handleRejectConfirm = async (reason) => {
    if (!rejectingLeave) return;
    const response = await dispatch(action.reject({ id: rejectingLeave._id, reason }));
    if (response) handleRefresh();
    setRejectingLeave(null);
  };
  //Yüklenen dosyayı görüntüleme.
  const handleViewAttachment = async (contact) => {
    if (!contact?.attachment?.filename) {
      toast.error(t("list.fileNotFoundError"));
      return;
    }

    const result = await dispatch(action.downloadAttachment(contact._id));
    if (!result) return;

    const blobUrl = URL.createObjectURL(result.data);
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  };

  const handleFilter = () => {
    let result = formLeave || [];

    if (filterName) {
      const lower = filterName.toLowerCase();
      result = result.filter((l) => {
        const fullName = `${l?.owner?.name} ${l?.owner?.surname}`.toLowerCase();
        const tc = l?.owner?.tc?.toLowerCase() || "";
        return fullName.includes(lower) || tc.includes(lower);
      });
    }

    if (filterMonth) {
      const [year, month] = filterMonth.split("-").map(Number);
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd = new Date(year, month, 0);
      result = result.filter((l) => {
        const start = new Date(l?.startDate);
        const end = new Date(l?.finishDate);
        return start <= monthEnd && end >= monthStart;
      });
    } else {
      if (filterStart) {
        result = result.filter((l) => new Date(l?.finishDate) >= new Date(filterStart));
      }
      if (filterEnd) {
        result = result.filter((l) => new Date(l?.startDate) <= new Date(filterEnd));
      }
    }


    if (filterType) {
      result = result.filter((l) => l?.leavesType === filterType);
    }

    setFiltered(result);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFilterName("");
    setFilterStart("");
    setFilterEnd("");
    setFilterType("");
    setFilterMonth("");
    setFiltered(formLeave || []);
    setCurrentPage(1);
  };

  const displayList = filtered;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayList.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Toplu onay/red yalnızca bekleyen talepler için anlamlıdır
  const pendingIdsOnPage = currentItems
    .filter((item) => item.state === LeaveStateEnum.PENDING)
    .map((item) => item._id);
  const allPendingSelected = pendingIdsOnPage.length > 0 && pendingIdsOnPage.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      allPendingSelected
        ? prev.filter((id) => !pendingIdsOnPage.includes(id))
        : Array.from(new Set([...prev, ...pendingIdsOnPage]))
    );
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };
  const handleDeleteOldAttachments = async () => {
    if (!deleteDate) {
      toast.error(t("list.selectDateError"));
      return;
    }
    if (!window.confirm(t("list.confirmDeleteOld"))) return;

    const leaves = displayList.filter((leave) => leave.attachment?.filename && new Date(leave.createdAt) < new Date(deleteDate));
    if (leaves.length === 0) {
      toast.info(t("list.noOldFilesFound"));
      return;
    }

    let deletedCount = 0;
    for (const leave of leaves) {
      if (await dispatch(action.deleteAttachment(leave._id, { silent: true }))) deletedCount++;
    }
    handleRefresh();

    if (deletedCount === leaves.length) {
      toast.success(t("list.deleteOldSuccess", { count: deletedCount }));
    } else {
      toast.error(t("list.deleteOldPartial", { count: deletedCount, total: leaves.length }));
    }
  };

  const closeCreateModal = () => setIsCreateModalOpen(false);
  const handleLeaveCreated = () => {
    closeCreateModal();
    handleRefresh();
  };

  const handleAttachmentDelete = async (leave) => {
    const response = await dispatch(action.deleteAttachment(leave._id));
    if (response) handleRefresh();
  };

  const bulkApprove = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(t("list.confirmBulkApprove", { count: selectedIds.length }));
    if (!confirmed) return;
    for (const id of selectedIds) {
      await dispatch(action.approve({ _id: id }));
    }
    handleRefresh();
    setSelectedIds([]);
  };

  const bulkReject = () => {
    if (selectedIds.length === 0) return;
    const reason = prompt(t("list.rejectReasonPrompt"));
    if (reason === null) return;
    selectedIds.forEach((id) => {
      dispatch(action.reject({ id, reason }));
    });
    handleRefresh();
    setSelectedIds([]);
  };

  // Excel ve PDF dışa aktarımında ortak kullanılan sütunlar
  const getExportColumns = () => [
    { label: t("dashboard.fullName"), width: 25, value: (item) => `${item?.owner?.name || ""} ${item?.owner?.surname || ""}`.trim() },
    { label: t("dashboard.tckn"), width: 15, value: (item) => item?.owner?.tc || "" },
    { label: t("dashboard.type"), width: 15, value: (item) => leaveTypeLabel(t, item?.leavesType) },
    { label: t("list.startDate"), width: 12, value: (item) => formatDate(item?.startDate) },
    { label: t("list.endDate"), width: 12, value: (item) => formatDate(item?.finishDate) },
    { label: t("list.tableDays"), width: 8, value: (item) => item?.totalDate ?? "" },
    { label: t("list.tableStatus"), width: 12, value: (item) => t(`leaveState.${leaveStateKey(item?.state)}`) },
  ];

  const exportToExcel = () => {
    const exportColumns = getExportColumns();
    const dataToExport = displayList.map((item) =>
      Object.fromEntries(exportColumns.map((col) => [col.label, col.value(item)]))
    );
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    ws['!cols'] = exportColumns.map((col) => ({ wch: col.width }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("list.excelSheetName"));
    XLSX.writeFile(wb, `leaves_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportToPDF = () => {
    const exportColumns = getExportColumns();
    const element = document.createElement('div');
    element.innerHTML = `
      <div style="font-family: Arial, sans-serif; padding: 10px;">
        <h2 style="text-align: center; margin-bottom: 10px;">${escapeHtml(t("list.pdfTitle"))}</h2>
        <table style="width:100%; border-collapse: collapse;">
          <thead>
            <tr>
              ${exportColumns.map((col) => `<th style="border:1px solid black; padding:6px; background-color: #e0e0e0;">${escapeHtml(col.label)}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${displayList.map(item => `
              <tr>
                ${exportColumns.map((col) => `<td style="border:1px solid black; padding:4px;">${escapeHtml(col.value(item))}</td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    html2pdf().from(element).set({
            margin: [0.5, 0.5, 0.5, 0.5],
      filename: `leaves_${new Date().toISOString().slice(0, 10)}.pdf`,
      html2canvas: { scale: 2, letterRendering: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }
    }).save();
  };

  return (
    <>
      <div className="w-[700px] mx-auto pt-[30px] flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">{t("list.title")}</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 shadow"
        >
          <Plus size={18} />
          {t("list.createLeave")}
        </button>
      </div>
      <div className="flex flex-col justify-center w-[700px] mx-auto pb-[30px]">
        {/* Filtre Alanı */}
        <div className="flex flex-wrap gap-2 mb-5 p-4 border rounded-lg bg-white">
          <input
            type="text"
            placeholder={t("list.searchPlaceholder")}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className="border rounded px-3 py-2 flex-1 min-w-[140px]"
          />
        <input
            type="month"
            value={filterMonth}
            onChange={(e) => { setFilterMonth(e.target.value); setFilterStart(""); setFilterEnd(""); }}
            className="border rounded px-3 py-2 flex-1 min-w-[140px]"
            title={t("list.selectMonth")}
          /> 
          <input
            type="date"
            value={filterStart}
            onChange={(e) => { setFilterStart(e.target.value); setFilterMonth(""); }}
            className="border rounded px-3 py-2 flex-1 min-w-[140px]"
            placeholder={t("list.startDate")}
          />
          <input
            type="date"
            value={filterEnd}
            onChange={(e) => { setFilterEnd(e.target.value); setFilterMonth(""); }}
            className="border rounded px-3 py-2 flex-1 min-w-[140px]"
            placeholder={t("list.endDate")}
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border rounded px-3 py-2 flex-1 min-w-[140px]"
          >
            <option value="">{t("list.tableType")}</option>
            {LEAVE_TYPES.map((type) => (
              <option key={type} value={type}>{leaveTypeLabel(t, type)}</option>
            ))}
          </select>
          <button onClick={handleFilter} className="bg-select-b text-white px-5 py-2 rounded">{t("list.filter")}</button>
          <button onClick={handleReset} className="border border-gray-300 text-gray-600 px-5 py-2 rounded">{t("list.reset")}</button>
          <button onClick={exportToExcel} className="bg-green-600 text-white px-5 py-2 rounded hover:bg-green-700">{t("list.buttonExcel")}</button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-5 py-2 rounded hover:bg-red-700">PDF</button>
        </div>

        {/* Toplu işlem çubuğu */}
        {displayList.length > 0 && (
          <div className="flex items-center justify-between mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer select-none text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  className="rounded text-blue-600 focus:ring-blue-500"
                  checked={allPendingSelected}
                  onChange={toggleSelectAll}
                />
                {t("list.selectAll")}
              </label>
              <span className="text-sm text-gray-500 font-medium">{t("list.selectedCount")}: {selectedIds.length}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={bulkApprove} disabled={selectedIds.length === 0} className={bulkButtonClass("bg-green-600 hover:bg-green-700")}>
                {t("list.bulkApprove")}
              </button>
              <button onClick={bulkReject} disabled={selectedIds.length === 0} className={bulkButtonClass("bg-red-600 hover:bg-red-700")}>
                {t("list.bulkReject")}
              </button>
            </div>
          </div>
        )}
        {/* Seçilen tarihten eski belgeleri sil — sadece admin */}
        {isAdmin && displayList.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
            <span className="text-sm text-gray-700 font-medium">{t("list.deleteOldLabel")}</span>
            <div className="flex items-center gap-3">
              <input
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-44 focus:ring-2 focus:ring-red-400 focus:border-red-400 focus:outline-none shadow-inner"
                type="date"
                value={deleteDate}
                onChange={(e) => setDeleteDate(e.target.value)}
              />
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-semibold shadow-sm transition-colors whitespace-nowrap active:scale-95 duration-150"
                onClick={handleDeleteOldAttachments}
              >
                {t("list.deleteOld")}
              </button>
            </div>
          </div>
        )}

        {/* Liste */}
        {!displayList || displayList.length === 0 ? (
          <p className="text-center text-gray-500">{t("dashboard.noRequests")}</p>
        ) : (
          <ul>
            {currentItems.map((contact, i) => {
              const isPending = contact?.state === LeaveStateEnum.PENDING;
              const isRejected = contact?.state === LeaveStateEnum.REJECTED;
              const isOwner = auth?.tc === contact?.owner?.tc;
              const hasFile = Boolean(contact?.attachment?.filename);
              // Kullanıcı kendi bekleyen talebini iptal edebilir; reddedilen talepler silinebilir
              const canDelete = isRejected || (isPending && isOwner);

              return (
                <div key={contact._id || i} className={`flex px-4 py-4 justify-between mb-3 ${i % 2 === 0 ? "bg-white" : "bg-input-bg"}`}>
                  <div className="flex items-center mr-2">
                    <input
                      type="checkbox"
                      disabled={!isPending}
                      checked={selectedIds.includes(contact._id)}
                      onChange={() => toggleSelect(contact._id)}
                    />
                  </div>
                  <div className="flex-1">
                    <li className="font-semibold text-lg flex items-center flex-wrap">
                      <span className="pr-1">{contact?.owner?.name || "-"}</span>
                      {contact?.owner?.surname || "-"}
                      <span className={`mx-5 font-[300] text-sm px-3 py-1 rounded-lg ${stateBadgeClass[leaveStateKey(contact?.state)]}`}>
                        {t(`leaveState.${leaveStateKey(contact?.state)}`)}
                      </span>
                    </li>
                    <li><span className="text-secondary-text pr-3">{t("dashboard.tckn")}:</span>{contact?.owner?.tc || "-"}</li>
                                        <li className="text-sm text-gray-600 mt-1">
                      <span className="text-secondary-text">{t("list.tableDates")}:</span>{" "}
                      {formatDate(contact?.startDate) || "-"}{" "}-{" "}
                      {formatDate(contact?.finishDate) || "-"}
                      <span className="ml-3 text-secondary-text">{t("list.tableDays")}:</span>{" "}{contact?.totalDate || "-"}
                    </li>
                  </div>
                  <div className="flex gap-x-3 items-center">
                    <button onClick={() => handleInfoClick(contact)} type="button" className="bg-select-b px-2 h-10 rounded-md text-white">
                      <img src={info} alt="info" />
                    </button>
                    {selectedInfo && <InfoModal contact={selectedInfo} onClose={handleCloseModal} />}
                    <a href={`/detaylar/${contact?._id}`} className="text-select-b flex items-center border-select-b border-[1px] px-4 h-10 rounded-md">{t("list.buttonDetail")}</a>
                    <div className="flex gap-2">
                      {hasFile && (isAdmin || isOwner) && (
                        <button
                          type="button"
                          onClick={() => handleViewAttachment(contact)}
                          className="bg-blue-500 border-blue-500 border-[1px] px-2 h-10 rounded-md text-white hover:bg-blue-600"
                          title={t("list.viewFile")}
                        >
                          📄
                        </button>
                      )}
                      {isAdmin && hasFile && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(t("list.confirmDeleteFile"))) handleAttachmentDelete(contact);
                          }}
                          className="bg-red-500 border-red-500 border-[1px] px-2 h-10 rounded-md text-white hover:bg-red-600"
                          title={t("list.deleteFile")}
                        >
                          🗑️
                        </button>
                      )}
                      {isAdmin && isPending && (
                        <button onClick={() => approves(contact)} className="bg-green-500 border-green-500 border-[1px] px-2 h-10 rounded-md text-white">
                          <img src={check} alt="check" />
                        </button>
                      )}
                      {isAdmin && !isRejected && (
                        <button onClick={() => openRejectModal(contact)} className="bg-red-500 px-4 h-10 rounded-md text-white">X</button>
                      )}
                      {canDelete && (
                        <button onClick={() => deletes(contact)} className="bg-red-500 px-4 h-10 rounded-md text-white border-[1px] border-white">
                          <img src={deleteIcon} alt="" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </ul>
        )}

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={`px-3 py-1 rounded border ${currentPage === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-white text-blue-600 hover:bg-blue-50"}`}>{t("list.prev")}</button>
            {[...Array(totalPages)].map((_, i) => (
              <button key={i} onClick={() => paginate(i + 1)} className={`px-3 py-1 rounded border ${currentPage === i + 1 ? "bg-blue-600 text-white" : "bg-white text-blue-600 hover:bg-blue-50"}`}>{i + 1}</button>
            ))}
            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className={`px-3 py-1 rounded border ${currentPage === totalPages ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-white text-blue-600 hover:bg-blue-50"}`}>{t("list.next")}</button>
          </div>
        )}
      </div>

      <RejectReasonModal open={!!rejectingLeave} onClose={() => setRejectingLeave(null)} onConfirm={handleRejectConfirm} />

      {isCreateModalOpen && (
        <div
          onClick={closeCreateModal}
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-start justify-center p-4 pt-10"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-2xl w-full p-6 relative border shadow-2xl"
          >
            <button
              onClick={closeCreateModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
              title={t("infoModal.close")}
            >
              <X size={24} />
            </button>
            {isAdmin ? (
              <CreateLeavePage onCancel={closeCreateModal} onSuccess={handleLeaveCreated} />
            ) : (
              <Form onCancel={closeCreateModal} onSuccess={handleLeaveCreated} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default List;
